import * as React from 'react'
import PropTypes from 'prop-types'
import { styled, alpha } from '@mui/material/styles'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'
import { blue, blueGrey, common, green, grey, orange } from '@mui/material/colors'

import BillingActive from 'assets/switch/active/Billing.svg'
import BillingDeactivated from 'assets/switch/deactivated/Billing.svg'

import ServiceActive from 'assets/switch/active/Service.svg'
import ServiceDeactivated from 'assets/switch/deactivated/Service.svg'

import AccountStatusActive from 'assets/switch/active/AccountStatus.svg'
import AccountStatusDeactivated from 'assets/switch/deactivated/AccountStatus.svg'

import PropertyOwnerActive from 'assets/switch/active/PropertyOwner.svg'
import PropertyOwnerDeactivated from 'assets/switch/deactivated/PropertyOwner.svg'

import UnscheduledActive from 'assets/switch/active/Unscheduled.svg'
import UnscheduledDeactivated from 'assets/switch/deactivated/Unscheduled.svg'

import TemporaryModeActive from 'assets/switch/active/TemporaryMode.svg'
import TemporaryModeDeactivated from 'assets/switch/deactivated/TemporaryMode.svg'

import DriverPathActive from 'assets/switch/active/DriverPath.svg'
import DriverPathDeactivated from 'assets/switch/deactivated/DriverPath.svg'

import SequenceLineActive from 'assets/switch/active/SequenceLine.svg'
import SequenceLineDeactivated from 'assets/switch/deactivated/SequenceLine.svg'

import InvoiceActive from 'assets/switch/active/Invoice.svg'
import InvoiceDeactivated from 'assets/switch/deactivated/Invoice.svg'

import TagActive from 'assets/switch/active/Tag.svg'
import TagDeactivated from 'assets/switch/deactivated/Tag.svg'

import ActionActive from 'assets/switch/active/Action.svg'
import ActionDeactivated from 'assets/switch/deactivated/Action.svg'

import UnassignedActive from 'assets/switch/active/Unassigned.svg'
import UnassignedDeactivated from 'assets/switch/deactivated/Unassigned.svg'

import EndedStopActive from 'assets/switch/active/EndedStop.svg'
import EndedStopDeactivated from 'assets/switch/deactivated/EndedStop.svg'

import DeactivatedStopActive from 'assets/switch/active/DeactivatedStop.svg'
import DeactivatedStopDeactivated from 'assets/switch/deactivated/DeactivatedStop.svg'

import { noop } from 'lodash'

// Two variants live in this file:
//   - variant="classic" (DEFAULT) — pre-redesign 62×34 track + 32×32 thumb,
//     active state uses a per-`type` color palette. Every legacy caller gets
//     this automatically; passing nothing keeps the historical look.
//   - variant="compact" — the master-sequence-revamp design: 48×24 track with
//     a 32×32 thumb that overflows ±4px, theme-aware track colour, and an
//     optional `Icon` prop that swaps the SVG-asset path for a coloured disc.

const ClassicStyledSwitch = styled(Switch)(({ activeIcon, deactivatedIcon, activeColor }) => ({
  width: 62,
  height: 34,
  padding: 7,
  margin: '0!important',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb': {
        backgroundColor: activeColor[50],
        border: '1px solid',
        borderColor: activeColor[500],
        '&:before': {
          backgroundImage: `url(${activeIcon})`,
        },
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.54)',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    backgroundColor: grey[50],
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.54)',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url(${deactivatedIcon})`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: grey[500],
    borderRadius: 20 / 2,
  },
}))

const ACCENT_GREEN = green[400]
const THUMB_BG_OFF = blueGrey[100]
const ICON_COLOR_OFF = blueGrey[600]
const ICON_SIZE = 16

const CompactStyledSwitch = styled(Switch)(({ theme, activeIcon, deactivatedIcon, hasIconProp }) => {
  const isDark = theme.palette.mode === 'dark'
  const trackOffColor = alpha(theme.palette.common.black, isDark ? 0.4 : 0.22)
  return {
    width: 48,
    height: 24,
    padding: 0,
    margin: '0!important',
    overflow: 'visible',
    '& .MuiSwitch-switchBase': {
      margin: 0,
      padding: 0,
      transform: 'translate(-4px, -4px)',
      '&.Mui-checked': {
        color: theme.palette.common.white,
        transform: 'translate(20px, -4px)',
        ...(!hasIconProp && {
          '& .MuiSwitch-thumb:before': { backgroundImage: `url(${activeIcon})` },
        }),
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: alpha(green[400], 0.32),
        },
      },
      '&:hover, &.Mui-focusVisible': { backgroundColor: 'transparent' },
    },
    ...(!hasIconProp && {
      '& .MuiSwitch-thumb': {
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.25)}`,
        border: 'none',
        width: 32,
        height: 32,
        backgroundColor: isDark ? 'transparent' : theme.palette.common.white,
        borderRadius: '50%',
        '&:before': {
          content: "''",
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundImage: `url(${deactivatedIcon})`,
        },
      },
    }),
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: trackOffColor,
      borderRadius: 12,
      transition: 'background-color 120ms ease',
    },
  }
})

const getSwitchIcons = type => {
  if (type === 'Billing') return { activeIcon: BillingActive, deactivatedIcon: BillingDeactivated }
  if (type === 'Service') return { activeIcon: ServiceActive, deactivatedIcon: ServiceDeactivated }
  if (type === 'AccountStatus') return { activeIcon: AccountStatusActive, deactivatedIcon: AccountStatusDeactivated }
  if (type === 'PropertyOwner') return { activeIcon: PropertyOwnerActive, deactivatedIcon: PropertyOwnerDeactivated }
  if (type === 'UnscheduledRoute') return { activeIcon: UnscheduledActive, deactivatedIcon: UnscheduledDeactivated }
  if (type === 'TemporaryMode') return { activeIcon: TemporaryModeActive, deactivatedIcon: TemporaryModeDeactivated }
  if (type === 'DriverPath') return { activeIcon: DriverPathActive, deactivatedIcon: DriverPathDeactivated }
  if (type === 'SequenceLine') return { activeIcon: SequenceLineActive, deactivatedIcon: SequenceLineDeactivated }
  if (type === 'Invoice') return { activeIcon: InvoiceActive, deactivatedIcon: InvoiceDeactivated }
  if (type === 'Tag') return { activeIcon: TagActive, deactivatedIcon: TagDeactivated }
  if (type === 'Action') return { activeIcon: ActionActive, deactivatedIcon: ActionDeactivated }
  if (type === 'UnassignedStop') return { activeIcon: UnassignedActive, deactivatedIcon: UnassignedDeactivated }
  if (type === 'EndedStop') return { activeIcon: EndedStopActive, deactivatedIcon: EndedStopDeactivated }
  if (type === 'DeactivatedStop') return { activeIcon: DeactivatedStopActive, deactivatedIcon: DeactivatedStopDeactivated }
  return { activeIcon: '', deactivatedIcon: '' }
}

const getActiveColor = type => {
  if (type === 'UnscheduledRoute') return blue
  if (type === 'TemporaryMode') return orange
  return green
}

const renderIconDisc = (Icon, checked) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      bgcolor: checked ? ACCENT_GREEN : THUMB_BG_OFF,
      display: 'grid',
      placeItems: 'center',
      boxShadow: `0 1px 3px ${alpha(common.black, 0.25)}`,
      transition: 'background-color 140ms ease',
    }}
  >
    <Icon sx={{ fontSize: ICON_SIZE, color: checked ? common.white : ICON_COLOR_OFF }} />
  </Box>
)

const HHSwitchIconField = ({ variant = 'classic', type, label, labelPlacement = 'start', isChecked, onChange = noop, Icon }) => {
  const { activeIcon, deactivatedIcon } = getSwitchIcons(type)

  if (variant === 'compact') {
    const iconProps = Icon ? { icon: renderIconDisc(Icon, false), checkedIcon: renderIconDisc(Icon, true) } : {}
    return (
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <CompactStyledSwitch
            activeIcon={activeIcon}
            deactivatedIcon={deactivatedIcon}
            hasIconProp={Boolean(Icon)}
            sx={{ mx: 1 }}
            disableRipple
            checked={isChecked}
            onChange={event => onChange(event.target.checked)}
            {...iconProps}
          />
        }
        label={label}
        labelPlacement={labelPlacement}
      />
    )
  }

  const activeColor = getActiveColor(type)
  return (
    <FormControlLabel
      sx={{ m: 0 }}
      control={
        <ClassicStyledSwitch
          activeColor={activeColor}
          activeIcon={activeIcon}
          deactivatedIcon={deactivatedIcon}
          sx={{ m: 1 }}
          checked={isChecked}
          onChange={event => onChange(event.target.checked)}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
    />
  )
}

HHSwitchIconField.propTypes = {
  variant: PropTypes.oneOf(['classic', 'compact']),
  type: PropTypes.string,
  label: PropTypes.string,
  labelPlacement: PropTypes.string,
  isChecked: PropTypes.bool,
  onChange: PropTypes.func,
  Icon: PropTypes.elementType,
}

export default HHSwitchIconField
